﻿import React, { useRef, useState } from 'react';
import ChevronRight from '../../../assets/icons/svg/ChevronRight';
import ChevronLeft from '../../../assets/icons/svg/ChevronLeft';
import { Header } from '../Header/Header';
import { Icon } from '../Icon/Icon';
import { DynamicTile } from '../Tiles/DynamicTile';
import { TileProps, TileTypes } from '../Tiles/tiles';
import SliderIndicator from './SliderIndicator';
import { getLoadingTiles } from '../../helpers/tileHelper';
import { Link } from '../Link/Link';
import { ExpandCategory } from '../../modules/Sliders/Slider.entry';
import { Asset } from '../../apiTypes/GameService';
import { SitecoreImage } from '../../modules/GameLauncher/GameLauncher';
import { isDesktop } from '@SharedScripts/Utils/DeviceClass';
import useBreakpoint from '@SharedScripts/Hooks/useBreakpoint';
import DsSlider, { goToSlide } from '@SharedScripts/Libraries/DsSlider';
import { track } from '../../helpers/tracking';
import { loadingStates } from '../Tiles/LoadingStates';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

export interface SliderProps {
  className?: string;
  sectionName: string;
  type: Exclude<TileTypes, 'Mega'>;
  games: TileProps[];
  assets?: Asset[] | SitecoreImage[];
  isLoading?: boolean;
  expandCategory?: ExpandCategory;
  expandLinkLocation?: SitecoreLink;
  showTitle?: boolean;
}

export type SitecoreLink = {
  anchor?: string,
  class?: string,
  text: string,
  query?: string,
  title?: string,
  url: string,
  target?: string,
  targetId: string
}

const Slider = ({
  sectionName,
  type,
  className = '',
  showTitle = true,
  games,
  isLoading,
  assets,
  expandLinkLocation
}: SliderProps) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [groupSize, setGroupSize] = useState<number>(0);
  const sliderLength = games.length ? games.length : assets?.length || 0;
  const breakpoint = useBreakpoint();
  const [isVisible, elementRef] = useIntersectionObserver<HTMLDivElement>({ threshold: 0 });

  const sliderRef = useRef(null);
  const next = useRef(null);
  const prev = useRef(null);
  const loadingTiles = getLoadingTiles();

  const setActive = (index: number) => {
    goToSlide(sliderRef, index);
  };

  const tileStyles = {
    Mega:
      'shrink-0 transition-opacity duration-300 ease-in-out w-(--grid-columns-12) md:w-(--grid-columns-5) lg:w-(--grid-columns-4)',
    Large:
      'shrink-0 transition-opacity duration-300 ease-in-out w-(--grid-columns-12) sm:w-(--grid-columns-6) md:w-(--grid-columns-5) lg:w-(--grid-columns-4) ' +
      'is-desktop-detected:w-[calc(var(--grid-columns-12)_-_4px)] is-desktop-detected:md:w-(--grid-columns-6) is-desktop-detected:xl:w-(--grid-columns-4) ',
    Numbered:
      'shrink-0 transition-opacity duration-300 ease-in-out w-(--grid-columns-6) sm:w-(--grid-columns-4) md:w-(--grid-columns-3) lg:w-(--grid-columns-2) ' +
      'is-desktop-detected:w-[calc(var(--grid-columns-12)_-_4px)] is-desktop-detected:sm:w-(--grid-columns-6) is-desktop-detected:md:w-(--grid-columns-4) is-desktop-detected:lg:w-(--grid-columns-3) is-desktop-detected:xl:w-(--grid-columns-2)',
    Medium:
      'shrink-0 transition-opacity duration-300 ease-in-out w-(--grid-columns-6) sm:w-(--grid-columns-4) md:w-(--grid-columns-3) lg:w-(--grid-columns-2) ' +
      'is-desktop-detected:w-[calc(var(--grid-columns-12)_-_4px)] is-desktop-detected:sm:w-(--grid-columns-6) is-desktop-detected:md:w-(--grid-columns-4) is-desktop-detected:lg:w-(--grid-columns-3) is-desktop-detected:xl:w-(--grid-columns-2)',
    Small: '',
    Micro: '',
    Winner: '',
    Asset: 'shrink-0 transition-opacity duration-300 ease-in-out w-(--grid-columns-11) md:w-(--grid-columns-5) lg:w-(--grid-columns-4)'
  } as const;

  const dynamicStyle = tileStyles?.[type];

  const typeToShow = () => {
    if (assets?.length) return 'assets';
    return (isLoading || !games?.length) ? 'loadingTiles' : 'games';
  };

  const dotsCount = () => {
    if (!sliderLength) return 0;
    if (breakpoint === 'sm') return sliderLength - 1;
    if (breakpoint === 'md') return sliderLength - 2;
    return sliderLength;
  };

  return (
    <section
      ref={elementRef}
      className={`overflow-hidden mx-auto pb-32 lg:pb-40 w-screen min-h-[316px] -ml-(--grid-margin) px-(--grid-margin) is-desktop-detected:w-[calc(100%_+_0.4rem)] is-desktop-detected:mx-auto is-desktop-detected:pr-0 is-desktop-detected:pl-[3px] ${className}`}
    >
      <div className='relative flex flex-col items-center'>
        <div className='flex items-center justify-between w-full mb-12'>
          <Header
            as={'h2'}
            size={'sm'}
          >
            {(showTitle && sectionName) ?? ''}
          </Header>
          <div className={'flex gap-24 items-center'}>

            {
              expandLinkLocation?.url && <div data-tracking-meta-data={JSON.stringify({
                action: 'see all click',
                label: 'Se alle',
                category: sectionName ?? ''
              })} onClick={track}
              >
                <Link
                  className={'font-family-base text-14 font-bold text-neutrals-grey-950'}
                  href={expandLinkLocation.url}
                >Se alle</Link>
              </div>
            }

            <div
              className={`flex gap-8 ${(groupSize) >= sliderLength ? 'hidden' : ''} ${!isDesktop() ? 'hidden' : ''}`}
            >
              <button
                ref={prev}
                className='flex items-center justify-center p-0 border-none rounded-full bg-neutrals-coolGrey-200 size-32 active:translate-y-1 focus-visible:ring-primary'
                data-tracking-meta-data={JSON.stringify({
                  action: 'left arrow click',
                  label: '<',
                  category: sectionName ?? ''
                })}
                onClick={track}
              >
                <Icon
                  className='*:stroke-neutrals-grey-950'
                  icon={<ChevronLeft/>}
                  size={'large'}
                ></Icon>
              </button>
              <button
                ref={next}
                className={
                  'bg-neutrals-coolGrey-200 border-none rounded-full size-32 flex justify-center items-center p-0 active:translate-y-1 focus-visible:ring-primary'
                }
                data-tracking-meta-data={JSON.stringify({
                  action: 'right arrow click',
                  label: '>',
                  category: sectionName ?? ''
                })}
                onClick={track}
              >
                <Icon
                  className={'*:stroke-neutrals-grey-950'}
                  icon={<ChevronRight/>}
                  size={'large'}
                ></Icon>
              </button>
            </div>
          </div>
        </div>
        <div
          className='w-full max-w-screen'
        >

          {(typeToShow() === 'loadingTiles') &&
            <div className={'flex gap-(--grid-gutter) w-fit'}>
              {
                loadingTiles.map((game, index) => {
                  return <div key={`${index}`}>
                    {loadingStates[type](game)}
                  </div>;
                })
              }
            </div>
          }

          {isVisible && typeToShow() !== 'loadingTiles' &&
            <DsSlider
              ref={sliderRef}
              slideBy={-1}
              beforeChange={setActiveSlide}
              onGroupSizeChange={setGroupSize}
              nextButton={next}
              previousButton={prev}
              className={`flex gap-(--grid-gutter) w-fit ${!isDesktop() ? 'pt-0.4 pb-[3.5rem]' : ''}`}
              touchMode={(breakpoint === 'md' || type !== 'Large') && !isDesktop() ? 'glide' : 'gesture'}
              buttonDisabledClasses={'*:*:opacity-20 pointer-events-none'}
            >
              {
                typeToShow() === 'games' && games.map((game, index) => (
                  <div
                    data-tracking-meta-data={JSON.stringify({
                      category: sectionName,
                      tileType: type,
                      gameTitle: game.title,
                      value: index + 1
                    })}
                    key={`${game.id}`}
                  >
                    <DynamicTile
                      type={type}
                      className={dynamicStyle}
                      data={{ ...game, index: index + 1 }}
                    />
                  </div>
                ))
              }
              {
                typeToShow() === 'assets' && assets?.map((asset, index) => (
                  <DynamicTile
                    key={index}
                    type={type}
                    className={dynamicStyle}
                    data={{
                      gameGraphics: { backgroundUrl: asset.url ?? undefined },
                      index: index + 1,
                      isLoading
                    }}
                  />
                ))
              }
            </DsSlider>
          }
        </div>
      </div>

      <div
        className={`${type === 'Large' ? 'md:hidden' : 'hidden'} mx-auto w-min ${games.length === 1 || assets?.length === 1 ? 'hidden' : ''}`}
      >
        <SliderIndicator
          length={dotsCount()}
          active={activeSlide}
          setActive={setActive}
        />
      </div>
    </section>
  );
};
export default Slider;
